import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

type Step = 'credentials' | 'totp' | 'email-otp';

export function Login() {
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justReset = searchParams.get('reset') === '1';

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.require2fa) {
        setTempToken(data.tempToken);
        // Mask the email for display: b***@mail.com
        const [local, domain] = email.split('@');
        setMaskedEmail(`${local[0]}***@${domain}`);
        setStep(data.method === 'email' ? 'email-otp' : 'totp');
      } else {
        navigate('/dashboard/inventory');
      }
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/2fa/confirm', { tempToken, code });
      navigate('/dashboard/inventory');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Invalid code');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  async function resendCode() {
    setResent(false);
    setError('');
    try {
      await api.post('/auth/2fa/resend', { tempToken });
      setResent(true);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to resend code');
    }
  }

  const Logo = () => (
    <div className="text-center mb-2">
      <div className="inline-flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center font-bold text-white">Cr</div>
        <div className="text-lg font-semibold tracking-tight">Cros<span className="text-secondary-400">ova</span></div>
      </div>
    </div>
  );

  if (step === 'credentials') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base">
        <form onSubmit={submitCredentials} className="card w-full max-w-[400px] p-6 sm:p-8 space-y-4 mx-4">
          <Logo />
          <div className="text-center">
            <h1 className="text-lg font-semibold">Welcome back</h1>
            <p className="text-xs text-white/40 mt-1">Log in to your seller account</p>
          {justReset && <p className="text-xs text-secondary-400 mt-1">Password updated — log in with your new password</p>}
          </div>
          <input
            className="input"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            className="input"
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && <div className="text-xs text-orange-400">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Logging in...' : 'Log in'}
          </button>
          <div className="flex items-center justify-between text-xs text-white/40">
            <Link to="/forgot-password" className="hover:text-white/60 transition-colors">Forgot password?</Link>
            <span>No account? <Link to="/signup" className="text-primary-300">Sign up</Link></span>
          </div>
        </form>
      </div>
    );
  }

  if (step === 'email-otp') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base">
        <form onSubmit={submitCode} className="card w-full max-w-[400px] p-6 sm:p-8 space-y-4 mx-4">
          <Logo />
          <div className="text-center">
            <h1 className="text-lg font-semibold">Check your email</h1>
            <p className="text-xs text-white/40 mt-1">
              We sent a 6-digit code to <span className="text-white/60">{maskedEmail}</span>
            </p>
          </div>
          <input
            className="input text-center tracking-[0.3em] text-lg font-mono"
            placeholder="000000"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            required
          />
          <p className="text-[11px] text-white/30 text-center">
            Code expires in 10 minutes. Lost your device? Use a backup code instead.
          </p>
          {error && <div className="text-xs text-orange-400 text-center">{error}</div>}
          {resent && <div className="text-xs text-secondary-400 text-center">Code resent!</div>}
          <button type="submit" disabled={loading || code.length < 6} className="btn-primary w-full">
            {loading ? 'Verifying...' : 'Verify'}
          </button>
          <div className="flex items-center justify-between text-xs text-white/30">
            <button type="button" onClick={resendCode} className="hover:text-white/60 transition-colors">
              Resend code
            </button>
            <button type="button" onClick={() => { setStep('credentials'); setError(''); setCode(''); }} className="hover:text-white/60 transition-colors">
              ← Back
            </button>
          </div>
        </form>
      </div>
    );
  }

  // TOTP step
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base">
      <form onSubmit={submitCode} className="card w-full max-w-[400px] p-6 sm:p-8 space-y-4 mx-4">
        <Logo />
        <div className="text-center">
          <h1 className="text-lg font-semibold">Authenticator code</h1>
          <p className="text-xs text-white/40 mt-1">Enter the 6-digit code from your authenticator app</p>
        </div>
        <input
          className="input text-center tracking-[0.3em] text-lg font-mono"
          placeholder="000000"
          type="text"
          inputMode="numeric"
          maxLength={8}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          autoFocus
          required
        />
        <p className="text-[11px] text-white/30 text-center">
          Lost your device? Enter one of your backup codes instead.
        </p>
        {error && <div className="text-xs text-orange-400 text-center">{error}</div>}
        <button type="submit" disabled={loading || code.length < 6} className="btn-primary w-full">
          {loading ? 'Verifying...' : 'Verify'}
        </button>
        <button
          type="button"
          onClick={() => { setStep('credentials'); setError(''); setCode(''); }}
          className="w-full text-xs text-white/30 hover:text-white/60 transition-colors"
        >
          ← Back to login
        </button>
      </form>
    </div>
  );
}
